import { useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import AskUserMessage from './AskUserMessage'

interface AskUserQuestion {
  question: string
  input_type: 'text' | 'buttons' | 'dropdown'
  options?: string[]
}

interface Message {
  id: string
  role: string
  content: string
  created_at: string
  message_type?: string
  metadata?: AskUserQuestion
  answered?: string
}

interface ActivityEvent {
  id: string
  tool: string
  summary: string
  detail?: Record<string, unknown>
  turn?: number
  timestamp: number
}

interface Stats {
  duration_s: number
  num_turns: number
  cost_usd?: number
  stop_reason?: string
}

interface Props {
  messages: Message[]
  streaming?: boolean
  onAnswer?: (answer: string) => void
  activity?: ActivityEvent[]
  agentInfo?: { model?: string; tools?: string[] }
  stats?: Stats | null
}

function AgentAvatar() {
  return (
    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600/20">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
        <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z"/>
      </svg>
    </div>
  )
}

function isErrorMessage(content: string) {
  return content.startsWith('Error:')
}

function toolIcon(tool: string): string {
  switch (tool) {
    case 'Read': return '📖'
    case 'Edit': return '✏️'
    case 'Write': return '📝'
    case 'WebFetch': return '🌐'
    case 'WebSearch': return '🔍'
    case 'Thinking': return '💭'
    default: return '⚙'
  }
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  const [open, setOpen] = useState(false)
  const hasDetail = event.detail && Object.keys(event.detail).length > 0
  return (
    <div className="border-b border-gray-100 py-1.5 last:border-b-0">
      <button
        onClick={() => hasDetail && setOpen(o => !o)}
        className={`flex w-full items-start gap-2 text-left ${hasDetail ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'}`}
      >
        <span className="shrink-0 text-[11px]">{toolIcon(event.tool)}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[10px] leading-relaxed text-gray-600">
            {event.summary}
          </span>
        </span>
        {event.turn && <span className="shrink-0 text-[9px] text-gray-300">#{event.turn}</span>}
      </button>
      {open && hasDetail && (
        <div className="mt-1 ml-5 space-y-1 rounded bg-gray-50 p-2 font-mono text-[9px] leading-relaxed text-gray-500">
          {Object.entries(event.detail as Record<string, unknown>).map(([k, v]) => (
            <div key={k} className="flex gap-1.5">
              <span className="shrink-0 text-gray-400">{k}:</span>
              <span className="min-w-0 break-all whitespace-pre-wrap text-gray-600">{String(v).slice(0, 400)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StreamingIndicator({ activity = [], agentInfo }: { activity?: ActivityEvent[]; agentInfo?: { model?: string; tools?: string[] } }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000)
    return () => clearInterval(t)
  }, [])
  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  const currentActivity = activity[activity.length - 1]
  const recentActivity = activity.slice(-12)

  return (
    <div className="flex gap-3">
      <AgentAvatar />
      <div className="flex min-w-0 flex-1 flex-col gap-1 pt-1">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" style={{ animationDelay: '0ms' }} />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" style={{ animationDelay: '200ms' }} />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" style={{ animationDelay: '400ms' }} />
          <span className="ml-2 text-[11px] font-medium text-gray-600">{timeStr}</span>
          {agentInfo?.model && (
            <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
              {agentInfo.model.replace('claude-', '')}
            </span>
          )}
        </div>
        <span className="text-[11px] text-gray-500">
          {currentActivity?.summary || (elapsed < 10 ? 'Starting agent...' : 'Thinking...')}
        </span>
        {recentActivity.length > 0 && (
          <div className="mt-1 max-h-64 max-w-full overflow-y-auto rounded border border-gray-200 bg-white px-2 py-1">
            {recentActivity.map(ev => (
              <ActivityRow key={ev.id} event={ev} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function ChatPanel({ messages, streaming, onAnswer, activity, agentInfo, stats }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const lastMessage = messages[messages.length - 1]
  const hasStreamingContent = streaming && lastMessage?.id === 'streaming' && lastMessage.content.length > 0

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming])

  return (
    <div className="flex-1 space-y-5 overflow-y-auto p-5">
      {messages.map(msg => (
        <div key={msg.id} className="flex gap-3">
          {msg.role === 'user' ? (
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[11px] font-medium text-gray-700">
              U
            </div>
          ) : (
            <AgentAvatar />
          )}
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className={`text-sm font-medium ${msg.role === 'user' ? 'text-gray-900' : 'text-emerald-700'}`}>
                {msg.role === 'user' ? 'You' : 'Agent'}
              </span>
              <span className="text-[11px] text-gray-400">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            {msg.message_type === 'ask_user' && msg.metadata ? (
              <AskUserMessage
                question={msg.metadata}
                answered={msg.answered}
                onAnswer={onAnswer || (() => {})}
              />
            ) : isErrorMessage(msg.content) ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {msg.content}
              </div>
            ) : msg.role === 'assistant' ? (
              <div className="prose prose-sm prose-gray max-w-none text-gray-700 prose-headings:text-sm prose-headings:font-semibold prose-headings:text-gray-900 prose-strong:text-gray-900 prose-p:leading-relaxed prose-li:text-gray-700 prose-ul:my-1 prose-ol:my-1">
                <Markdown>{msg.content}</Markdown>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {msg.content}
              </div>
            )}
          </div>
        </div>
      ))}

      {streaming && !hasStreamingContent && (
        <StreamingIndicator activity={activity} agentInfo={agentInfo} />
      )}

      {!streaming && stats && (
        <div className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] text-gray-500">
          <span>✓ {stats.duration_s}s</span>
          <span>{stats.num_turns} turns</span>
          {stats.cost_usd != null && <span>${stats.cost_usd.toFixed(4)}</span>}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
